import React, { useState } from 'react';

/**
 * Fox and Geese board game component.
 *
 * The board is a cross-shaped grid with 33 valid points arranged in a 7×7
 * coordinate space. Geese move forward/sideways only; the fox moves freely
 * and captures by jumping over adjacent geese into empty squares.
 */

// All 33 valid board points in (row, col) coordinates on a 7×7 grid
const POINTS = [
	// Row 0: top arm
	{ row: 0, col: 2 }, { row: 0, col: 3 }, { row: 0, col: 4 },
	// Row 1: top arm
	{ row: 1, col: 2 }, { row: 1, col: 3 }, { row: 1, col: 4 },
	// Row 2: full row
	{ row: 2, col: 0 }, { row: 2, col: 1 }, { row: 2, col: 2 },
	{ row: 2, col: 3 }, { row: 2, col: 4 }, { row: 2, col: 5 }, { row: 2, col: 6 },
	// Row 3: centre row
	{ row: 3, col: 0 }, { row: 3, col: 1 }, { row: 3, col: 2 },
	{ row: 3, col: 3 }, { row: 3, col: 4 }, { row: 3, col: 5 }, { row: 3, col: 6 },
	// Row 4: full row
	{ row: 4, col: 0 }, { row: 4, col: 1 }, { row: 4, col: 2 },
	{ row: 4, col: 3 }, { row: 4, col: 4 }, { row: 4, col: 5 }, { row: 4, col: 6 },
	// Row 5: bottom arm
	{ row: 5, col: 2 }, { row: 5, col: 3 }, { row: 5, col: 4 },
	// Row 6: bottom arm
	{ row: 6, col: 2 }, { row: 6, col: 3 }, { row: 6, col: 4 },
];

// Map "row,col" -> index for quick lookups
const POS_TO_IDX = {};
POINTS.forEach(({ row, col }, i) => { POS_TO_IDX[`${row},${col}`] = i; });

/**
 * Adjacency list: two points are neighbours if their row and column each
 * differ by at most 1 (8-directional) and both are valid board points.
 */
const ADJACENCY = POINTS.map(({ row, col }) => {
	const neighbors = [];
	for (let dr = -1; dr <= 1; dr++) {
		for (let dc = -1; dc <= 1; dc++) {
			if (dr === 0 && dc === 0) continue;
			const key = `${row + dr},${col + dc}`;
			if (POS_TO_IDX[key] !== undefined) neighbors.push(POS_TO_IDX[key]);
		}
	}
	return neighbors;
});

// Index of the fox's starting position (row 3, col 3 – centre of the board)
const FOX_START = POS_TO_IDX['3,3']; // index 16

/**
 * Build the initial board state.
 * Geese (15) occupy:
 *   - Top arm rows 0–1, cols 2–4: indices 0–5
 *   - Full row 2 (cols 0–6): indices 6–12
 *   - Row 3 ends (cols 0 and 6): indices 13 and 19
 * Fox occupies the centre: index FOX_START.
 * @returns {Array} Initial board array of 33 entries
 */
const buildInitialBoard = () => {
	const board = Array(33).fill(null);
	for (let i = 0; i <= 12; i++) board[i] = 'goose'; // top arm + row 2
	board[13] = 'goose'; // row 3, col 0
	board[19] = 'goose'; // row 3, col 6
	board[FOX_START] = 'fox';
	return board;
};

// SVG layout constants
const CELL = 100; // pixels per grid cell
const PAD = 50;   // offset so each point sits at the centre of its cell
const R_PIECE = 48; // piece icon half-size

// Inline SVG content for Fox (front-facing head, cartoonish, cute)
const FoxContent = () => (
	<>
		{/* Left ear */}
		<path d="M 24 44 L 30 12 L 44 36 Z" fill="#E8881F"/>
		{/* Right ear */}
		<path d="M 76 44 L 70 12 L 56 36 Z" fill="#E8881F"/>
		{/* Inner left ear */}
		<path d="M 28 42 L 32 20 L 42 37 Z" fill="#F7C87D"/>
		{/* Inner right ear */}
		<path d="M 72 42 L 68 20 L 58 37 Z" fill="#F7C87D"/>
		{/* Head */}
		<circle cx="50" cy="54" r="30" fill="#E8881F"/>
		{/* Light face center */}
		<ellipse cx="50" cy="60" rx="20" ry="20" fill="#FADED0"/>
		{/* Cheek blush */}
		<circle cx="32" cy="60" r="6" fill="#F0A060" opacity="0.5"/>
		<circle cx="68" cy="60" r="6" fill="#F0A060" opacity="0.5"/>
		{/* Eyes */}
		<ellipse cx="40" cy="50" rx="4.5" ry="5.5" fill="#4A3728"/>
		<ellipse cx="60" cy="50" rx="4.5" ry="5.5" fill="#4A3728"/>
		{/* Eye shine */}
		<circle cx="42" cy="48" r="2" fill="white" opacity="0.8"/>
		<circle cx="62" cy="48" r="2" fill="white" opacity="0.8"/>
		{/* Nose */}
		<ellipse cx="50" cy="63" rx="4" ry="3" fill="#4A3728"/>
		{/* Nose highlight */}
		<ellipse cx="49" cy="62" rx="1.5" ry="1" fill="white" opacity="0.3"/>
		{/* Mouth */}
		<path d="M 46 66 Q 50 70 54 66" stroke="#B06030" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
	</>
);

// Inline SVG content for Goose (front-facing head, cartoonish, cute)
const GooseContent = () => (
	<>
		{/* Head */}
		<ellipse cx="50" cy="50" rx="28" ry="30" fill="#E8E8E4"/>
		{/* Forehead tuft */}
		<ellipse cx="50" cy="24" rx="8" ry="6" fill="#D0D0CC"/>
		{/* Cheek blush */}
		<circle cx="30" cy="56" r="6" fill="#F0B8A0" opacity="0.4"/>
		<circle cx="70" cy="56" r="6" fill="#F0B8A0" opacity="0.4"/>
		{/* Eyes */}
		<circle cx="38" cy="44" r="4.5" fill="#3A3A3A"/>
		<circle cx="62" cy="44" r="4.5" fill="#3A3A3A"/>
		{/* Eye shine */}
		<circle cx="40" cy="42" r="2" fill="white" opacity="0.8"/>
		<circle cx="64" cy="42" r="2" fill="white" opacity="0.8"/>
		{/* Beak */}
		<ellipse cx="50" cy="62" rx="10" ry="7" fill="#E8A030"/>
		{/* Beak upper ridge */}
		<ellipse cx="50" cy="59" rx="8" ry="4" fill="#F0B840"/>
		{/* Nostrils */}
		<circle cx="47" cy="61" r="1.2" fill="#C08020" opacity="0.6"/>
		<circle cx="53" cy="61" r="1.2" fill="#C08020" opacity="0.6"/>
		{/* Smile */}
		<path d="M 44 66 Q 50 70 56 66" stroke="#C08020" strokeWidth="1" fill="none" strokeLinecap="round"/>
	</>
);

/**
 * Fox and Geese game component.
 */
const FoxAndGeese = () => {
	const [board, setBoard] = useState(buildInitialBoard);
	const [currentTurn, setCurrentTurn] = useState('goose'); // 'goose' or 'fox'
	const [selectedIdx, setSelectedIdx] = useState(null);
	const [validMoves, setValidMoves] = useState([]);
	const [winner, setWinner] = useState(null); // 'fox' or 'goose'

	/**
	 * Returns valid destination indices for a goose at `from`.
	 * Geese may move forward (increasing row), diagonally forward, or sideways,
	 * but NOT backward (decreasing row).
	 * @param {number} from - Source index
	 * @param {Array} currentBoard - Current board state
	 * @returns {number[]} Valid destination indices
	 */
	const getGooseMoves = (from, currentBoard) => {
		const fromRow = POINTS[from].row;
		return ADJACENCY[from].filter(to => {
			if (currentBoard[to] !== null) return false;
			return POINTS[to].row >= fromRow; // no backward movement
		});
	};

	/**
	 * Returns valid destination indices for the fox at `from`.
	 * Includes normal adjacent moves to empty squares plus capture jumps
	 * (hop over an adjacent goose into the empty square behind it).
	 * @param {number} from - Source index
	 * @param {Array} currentBoard - Current board state
	 * @returns {number[]} Valid destination indices
	 */
	const getFoxMoves = (from, currentBoard) => {
		const moves = [];
		const foxRow = POINTS[from].row;
		const foxCol = POINTS[from].col;

		// Normal adjacent moves to empty squares
		for (const to of ADJACENCY[from]) {
			if (currentBoard[to] === null) moves.push(to);
		}

		// Capture moves: jump over an adjacent goose into an empty square
		for (const gooseIdx of ADJACENCY[from]) {
			if (currentBoard[gooseIdx] !== 'goose') continue;
			const dr = POINTS[gooseIdx].row - foxRow;
			const dc = POINTS[gooseIdx].col - foxCol;
			const landKey = `${POINTS[gooseIdx].row + dr},${POINTS[gooseIdx].col + dc}`;
			const landIdx = POS_TO_IDX[landKey];
			if (landIdx !== undefined && currentBoard[landIdx] === null) {
				moves.push(landIdx);
			}
		}
		return moves;
	};

	/**
	 * Check for a win condition after a move has been applied.
	 * @param {Array} newBoard - Board state after the move
	 * @param {'goose'|'fox'} nextTurn - Whose turn comes next
	 * @returns {'fox'|'goose'|null} Winner, or null if game continues
	 */
	const checkWinner = (newBoard, nextTurn) => {
		if (nextTurn === 'fox') {
			// Geese just moved – check if fox is trapped
			const foxIdx = newBoard.indexOf('fox');
			if (getFoxMoves(foxIdx, newBoard).length === 0) return 'goose';
		} else {
			// Fox just moved – check fox-wins conditions
			const geeseCount = newBoard.filter(c => c === 'goose').length;
			if (geeseCount < 3) return 'fox';
			const hasGooseMove = newBoard.some(
				(cell, i) => cell === 'goose' && getGooseMoves(i, newBoard).length > 0
			);
			if (!hasGooseMove) return 'fox';
		}
		return null;
	};

	/**
	 * Handle a click on board point at `idx`.
	 * @param {number} idx - Index of the clicked point
	 */
	const handlePointClick = (idx) => {
		if (winner) return;

		if (currentTurn === 'goose') {
			if (board[idx] === 'goose') {
				// Select or deselect a goose
				if (selectedIdx === idx) {
					setSelectedIdx(null);
					setValidMoves([]);
				} else {
					setSelectedIdx(idx);
					setValidMoves(getGooseMoves(idx, board));
				}
			} else if (selectedIdx !== null && validMoves.includes(idx)) {
				// Execute goose move
				const newBoard = [...board];
				newBoard[idx] = 'goose';
				newBoard[selectedIdx] = null;
				setBoard(newBoard);
				setSelectedIdx(null);
				setValidMoves([]);
				const w = checkWinner(newBoard, 'fox');
				if (w) {
					setWinner(w);
				} else {
					setCurrentTurn('fox');
				}
			}
		} else {
			// Fox's turn
			const foxIdx = board.indexOf('fox');
			if (selectedIdx === null) {
				if (idx === foxIdx) {
					setSelectedIdx(idx);
					setValidMoves(getFoxMoves(idx, board));
				}
			} else if (validMoves.includes(idx)) {
				// Execute fox move (may include a capture)
				const newBoard = [...board];
				const foxRow = POINTS[foxIdx].row;
				const foxCol = POINTS[foxIdx].col;
				const deltaRow = POINTS[idx].row - foxRow;
				const deltaCol = POINTS[idx].col - foxCol;

				// If the move is more than 1 step in any direction, it is a capture jump.
				// Capture jumps always span exactly 2 cells (deltaRow and deltaCol are
				// each 0 or ±2), so deltaRow/2 and deltaCol/2 are always integers.
				if (Math.abs(deltaRow) > 1 || Math.abs(deltaCol) > 1) {
					const midKey = `${foxRow + deltaRow / 2},${foxCol + deltaCol / 2}`;
					const midIdx = POS_TO_IDX[midKey];
					if (midIdx !== undefined && newBoard[midIdx] === 'goose') {
						newBoard[midIdx] = null;
					}
				}

				newBoard[foxIdx] = null;
				newBoard[idx] = 'fox';
				setBoard(newBoard);
				setSelectedIdx(null);
				setValidMoves([]);
				const w = checkWinner(newBoard, 'goose');
				if (w) {
					setWinner(w);
				} else {
					setCurrentTurn('goose');
				}
			} else if (idx === foxIdx) {
				// Deselect
				setSelectedIdx(null);
				setValidMoves([]);
			}
		}
	};

	/**
	 * Reset the game to its initial state.
	 */
	const resetGame = () => {
		setBoard(buildInitialBoard());
		setCurrentTurn('goose');
		setSelectedIdx(null);
		setValidMoves([]);
		setWinner(null);
	};

	/**
	 * Build the current status message.
	 * @returns {string} Status text
	 */
	const getStatus = () => {
		if (winner === 'fox') return 'Fox wins! The geese cannot trap the fox.';
		if (winner === 'goose') return 'Geese win! The fox is trapped.';
		if (currentTurn === 'goose') return "Geese's turn – select a goose to move";
		return "Fox's turn – select the fox to move";
	};

	return (
		<div className="game-container">
			<h1 className="game-title">Fox and Geese</h1>
			<div className={`game-status ${winner ? 'winner' : ''}`}>
				{getStatus()}
			</div>
			<svg
				viewBox={`0 0 ${7 * CELL} ${7 * CELL}`}
				className="fox-and-geese-svg"
				role="img"
				aria-label="Fox and Geese board"
			>
				{/* Draw lines between adjacent points */}
				{POINTS.map((pt, i) =>
					ADJACENCY[i].filter(j => j > i).map(j => (
						<line
							key={`${i}-${j}`}
							x1={pt.col * CELL + PAD}
							y1={pt.row * CELL + PAD}
							x2={POINTS[j].col * CELL + PAD}
							y2={POINTS[j].row * CELL + PAD}
							stroke="#8b7355"
							strokeWidth={2}
						/>
					))
				)}

				{/* Draw each point and its piece */}
				{POINTS.map((pt, i) => {
					const cx = pt.col * CELL + PAD;
					const cy = pt.row * CELL + PAD;
					const isSelected = selectedIdx === i;
					const isValid = validMoves.includes(i);
					const piece = board[i];
					const isClickable = !winner && (
						(currentTurn === 'goose' && (piece === 'goose' || (isValid && selectedIdx !== null))) ||
						(currentTurn === 'fox' && (piece === 'fox' || (isValid && selectedIdx !== null)))
					);

					return (
						<g
							key={i}
							onClick={() => handlePointClick(i)}
							style={{ cursor: isClickable ? 'pointer' : 'default' }}
							aria-label={piece ? `${piece === 'goose' ? 'Goose' : 'Fox'} at row ${pt.row} column ${pt.col}${isSelected ? ', selected' : ''}` : isValid ? `Valid move target at row ${pt.row} column ${pt.col}` : undefined}
							role={piece || isValid ? 'button' : undefined}
						>
							{/* Selection highlight */}
							{isSelected && (
								<circle cx={cx} cy={cy} r={R_PIECE + 6} fill="#facc15" opacity={0.55} />
							)}

							{/* Piece rendering */}
							{piece === 'goose' && (
								<svg x={cx - R_PIECE} y={cy - R_PIECE} width={R_PIECE * 2} height={R_PIECE * 2} viewBox="0 0 100 100">
									<GooseContent />
								</svg>
							)}
							{piece === 'fox' && (
								<svg x={cx - R_PIECE} y={cy - R_PIECE} width={R_PIECE * 2} height={R_PIECE * 2} viewBox="0 0 100 100">
									<FoxContent />
								</svg>
							)}

							{/* Valid-move indicator for empty points */}
							{isValid && !piece && (
								<circle cx={cx} cy={cy} r={10} fill="#4ade80" opacity={0.85} />
							)}

							{/* Empty-point dot */}
							{!piece && !isValid && (
								<circle cx={cx} cy={cy} r={5} fill="#8b7355" />
							)}
						</g>
					);
				})}
			</svg>
			<button className="new-game-button" onClick={resetGame}>
				New Game
			</button>
		</div>
	);
};

export default FoxAndGeese;
